import React, { useState } from 'react';
import { Field } from 'formik';
import _ from 'lodash';
import { useFocus } from '../hooks/useFocus';
import { PremiumLabelBig } from './ui/PremiumLabelBig';
import { PremiumLabelSmall } from './ui/PremiumLabelSmall';
import { PremiumUnit } from './ui/PremiumUnit';
import { PremiumInputContainer } from './ui/PremiumInputContainer';
import { LabelAndValue } from './ui/LabelAndValue';
import { UnitsForLabelAndValue } from './ui/UnitsForLabelAndValue';
import { Value } from './ui/Value';
import { RequiredIndicator } from './ui/RequiredIndicator';
import { Invisible } from './ui/Invisible';
import { RadioButton } from './ui/RadioButton';
import { DropdownContainer } from './ui/DropdownContainer';
import { DropdownItem } from './ui/DropdownItem';
import ReactDatePicker from 'react-datepicker';
import styled, { css } from 'styled-components';
import moment from 'moment';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import { ReactComponent as CalendarSearchIcon } from 'assets/icons/calendar-search.svg';
import Ball from '../ball/ball';
import useClickOutside from '../hooks/useClickOutside';
import 'react-datepicker/dist/react-datepicker.css';
import SelectWithSearchInput from './select-with-search-input';

const getProcessedValueForTimeInput = (value) => {
  try {
    const first5Chars = value.substring(0, 5);

    const withNoWeirdChars = first5Chars.replace(/[^\d:-]/g, '');
    const processedValue = withNoWeirdChars;
    const length = processedValue.length;

    // ******************************************************************************************
    // ** check position 0 X
    // ******************************************************************************************
    let pos0Value = null;
    let isPos0Valid = false;

    if (length > 0) {
      try {
        pos0Value = parseInt(value[0]);
      } catch (e) {}
      //   we can get from 0 to 9 here
      isPos0Valid = pos0Value >= 0 && pos0Value <= 9;
    }

    // if we have 1 positions we finished checking
    if (length === 1) {
      if (isPos0Valid) return processedValue;
      return '';
    }

    // we continue checking but if the first char is not valid we are done
    if (!isPos0Valid) return '';

    // ******************************************************************************************
    // ** check position 1 #X
    // ******************************************************************************************

    let pos1Value = null;
    let isPos1Valid = false;

    if (length > 1) {
      // we can get "":" in any moment here
      pos1Value = value[1];
      if (pos1Value === ':') isPos1Valid = true;
      // if we get a number
      else {
        try {
          pos1Value = parseInt(value[1]);
        } catch (e) {}
        // if first was 0 or 1, we can get any number here
        if (pos0Value === 0 || pos0Value === 1) {
          isPos1Valid = pos1Value >= 0 && pos1Value <= 9;
        }
        // if first was 2, we can get from 0 to 3 here (20-23)
        else if (pos0Value === 2) {
          isPos1Valid = pos1Value >= 0 && pos1Value <= 3;
        }
        // if we got a bigger number like 5 for the first char, the second char can not be a number
        // so is not valid
        else {
          isPos1Valid = false;
        }
      }
    }

    // if we have 2 positions we have to check if we already have ":", or add it
    if (length === 2) {
      if (isPos1Valid) {
        if (pos1Value !== ':') {
          return `${processedValue}:`;
        } else {
          return processedValue;
        }
      }
      return processedValue.substring(0, 1);
    }

    // ******************************************************************************************
    // ** check position 2 ##X
    // ******************************************************************************************
    let pos2Value = null;
    let isPos2Valid = false;

    if (length > 2) {
      //   if we get ":"
      pos2Value = value[2];
      if (pos2Value === ':') {
        // we can get ":" only if pos 1 is not ":"
        if (pos1Value === ':') isPos2Valid = false;
        else isPos2Valid = true;
      }
      // if we get a number
      else {
        // the 2nd position has to be ":"
        if (pos1Value !== ':') {
          isPos2Valid = false;
        }
        // if is a number it has to be beetwen 0 and 5
        else {
          try {
            pos2Value = parseInt(value[2]);
            isPos2Valid = pos2Value >= 0 && pos2Value <= 5;
          } catch (e) {}
        }
      }
    }

    // if we have 3 positions we finished checking
    if (length === 3) {
      if (isPos2Valid) return processedValue;
      return processedValue.substring(0, 2);
    }

    // ******************************************************************************************
    // ** check position 3 ##X
    // ******************************************************************************************
    let pos3Value = null;
    let isPos3Valid = false;

    if (length > 3) {
      // we can get only numbers here
      try {
        pos3Value = parseInt(value[3]);
        // if previous war chas ":" we can get between 0 and 5 here
        if (pos2Value === ':') {
          isPos3Valid = pos3Value >= 0 && pos3Value <= 5;
        }
        // if was not ":", it depends we can get between 0 and 9
        else {
          isPos3Valid = pos3Value >= 0 && pos3Value <= 9;
        }
      } catch (e) {}
    }

    // if we have 4 positions we have to check if the input is already filled (5:30)
    if (length === 4) {
      if (isPos3Valid) {
        // if we are in X:Z
        // and we get a valid number here
        // then we alreayd have X:ZY
        // so we add a 0 to the beggining and we are done
        const isLastNumber = pos1Value === ':';
        if (isLastNumber) {
          return `0${processedValue}`;
        } else {
          return processedValue;
        }
      }
      return processedValue.substring(0, 3);
    }

    // ******************************************************************************************
    // ** check position 4 ##X
    // ******************************************************************************************

    let pos4Value = null;
    let isPos4Valid = false;

    if (length > 4) {
      // we dont accept ":" here, only numbers
      try {
        pos4Value = parseInt(value[4]);
        isPos4Valid = pos4Value >= 0 && pos4Value <= 9;
      } catch (e) {}
    }

    // if we have 5 positions we finished checking
    if (length === 5) {
      if (isPos4Valid) return processedValue;
      return processedValue.substring(0, 4);
    }

    return processedValue;
  } catch (e) {
    console.error('🔥 getFormatedTimeForInput error ', e);
    return '';
  }
};

const DatepickerContainer = styled.div`
  z-index: 300;
  top: 95px;
  left: 30px;
  position: absolute;
  transform: scale(1.5);
  ${(props) =>
    !!props.fullWidth &&
    css`
      width: 100%;
      .react-datepicker {
        width: 100%;
      }
      .react-datepicker__time-container {
        width: 100%;
      }
      .react-datepicker__time-container
        .react-datepicker__time
        .react-datepicker__time-box {
        width: 100%;
      }
    `};
`;
const InputCleaner = styled.span`
  border-radius: 50%;
  background-color: black;
  opacity: 0.2;
  height: 16px;
  width: 16px;
  color: white;
  display: block;
  cursor: pointer;
  text-align: center;
`;

export const PremiumInput = ({ className, id, ...props }) => {
  const {
    label,
    type = 'text',
    units = null,
    value = null,
    min = 1,
    isDisabled = false,
    isRequired = false,
    hasError = false,
  } = props;
  const [hasFocus, setHasFocus] = useState(false);
  const clickRefFrom = React.useRef();
  useClickOutside(clickRefFrom, () => {
    setHasFocus(false);
  });
  const [inputRef, setInputFocus] = useFocus();

  const hasValue =
    value !== null && value !== '' && (value !== 0 || value >= min);

  const showInput = hasFocus || hasValue;
  const { field, form } = props;
  const { disableClear, showCalendarSearchIcon } = props;

  //   RADIO
  if (type === 'radio') {
    return (
      <RadioButton
        checked={props.checked}
        isDisabled={isDisabled}
        onClick={() => {
          if (isDisabled) return;
          form.setFieldValue(field.name, field.value);
        }}
      >
        {props.children}
      </RadioButton>
    );
  }

  // SELECT
  if (type === 'select') {
    const availableOptions = props.availableOptions;

    const selectedOption = availableOptions.find((l) => l.value === value);

    const setValue = (value, field) => {
      if (props.onChange !== undefined) {
        props.onChange(value, form);
      } else {
        form.setFieldValue(field.name, value);
      }
    };

    return (
      <>
        <PremiumInputContainer
          width={props.width}
          isDisabled={isDisabled}
          hasError={hasError}
          className={className}
          hasFocus={hasFocus}
          ref={clickRefFrom}
          onClick={() => {
            if (isDisabled) return;
            setHasFocus(true);
          }}
        >
          {hasFocus && (
            <DropdownContainer>
              {availableOptions.map((item) => (
                <DropdownItem
                  isSelected={item.value === value}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isDisabled) return;
                    setValue(item.value, field);
                    setHasFocus(false);
                  }}
                >
                  {item.color && <Ball backgroundColor={item.color} />}
                  {item.label}
                </DropdownItem>
              ))}
            </DropdownContainer>
          )}
          <LabelAndValue>
            <PremiumLabelSmall>
              {label}
              {isRequired && <RequiredIndicator />}
            </PremiumLabelSmall>
            <Value>
              {selectedOption?.label}
              <Invisible>
                <input
                  onFocus={() => {
                    setHasFocus(true);
                  }}
                  onBlur={() => {
                    setHasFocus(false);
                  }}
                />
              </Invisible>
            </Value>
          </LabelAndValue>
          <UnitsForLabelAndValue noPadding={true}>
            <PremiumUnit>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.9342 6.20291C10.8435 6.07757 10.6731 6.00002 10.4884 6H5.51147C5.32689 6.00007 5.15666 6.07757 5.06599 6.20282C4.97532 6.32807 4.97827 6.48165 5.0737 6.60473L7.56218 9.80778C7.65489 9.9271 7.82091 10 7.99996 10C8.179 10 8.34502 9.9271 8.43773 9.80778L10.9262 6.60473C11.0217 6.4817 11.0247 6.32816 10.9342 6.20291Z"
                  fill="#54708C"
                />
              </svg>
            </PremiumUnit>
          </UnitsForLabelAndValue>
        </PremiumInputContainer>
      </>
    );
  }

  //   date
  if (type === 'date') {
    const setValue = (date, field) => {
      if (props.onChange !== undefined) {
        props.onChange(date);
      } else {
        form.setFieldValue(field.name, date);
      }
    };
    const internalOnchange = (date, event) => {
      event.stopPropagation();
      const dateToSet = moment(date).format('YYYY-MM-DD');
      setValue(dateToSet, field);
      setHasFocus(false);
    };
    return (
      <>
        <PremiumInputContainer
          width={props.width}
          isDisabled={isDisabled}
          hasError={hasError}
          className={className}
          hasFocus={hasFocus}
          ref={clickRefFrom}
          onClick={() => {
            if (isDisabled) return;
            setHasFocus(true);
          }}
        >
          {hasFocus && (
            <DatepickerContainer>
              <ReactDatePicker onChange={internalOnchange} inline />
            </DatepickerContainer>
          )}
          <LabelAndValue>
            {hasValue ? (
              <PremiumLabelSmall>
                {label}
                {isRequired && <RequiredIndicator />}
              </PremiumLabelSmall>
            ) : (
              <PremiumLabelBig>
                {label}
                {isRequired && <RequiredIndicator />}
              </PremiumLabelBig>
            )}
            <Value>
              {value}
              <Invisible>
                <input
                  onFocus={() => {
                    setHasFocus(true);
                  }}
                  onBlur={() => {
                    setHasFocus(false);
                  }}
                />
              </Invisible>
            </Value>
          </LabelAndValue>
          <UnitsForLabelAndValue noPadding={true}>
            <PremiumUnit gap={'10px'}>
              {!disableClear && hasValue && !isDisabled && (
                <InputCleaner
                  onClick={(event) => {
                    event.stopPropagation();
                    form.setFieldValue(field.name, '');
                    setHasFocus(false);
                  }}
                >
                  x
                </InputCleaner>
              )}
              {!!showCalendarSearchIcon && <CalendarSearchIcon />}
            </PremiumUnit>
          </UnitsForLabelAndValue>
        </PremiumInputContainer>
      </>
    );
  }

  //   time
  if (type === 'time') {
    const setValue = (value, field) => {
      if (props.onChange !== undefined) {
        props.onChange(value);
      } else {
        form.setFieldValue(field.name, value);
      }
    };

    const customOnChange = (value, field) => {
      const processedValue = getProcessedValueForTimeInput(value);
      setValue(processedValue, field);
    };

    const customOnKeyDown = (event) => {
      const keyPressed = event.key;
      const currentValue = event.target.value;
      const currentNumberOfPositions = currentValue.length;

      // for backspace we need to see what positions to remove
      if (keyPressed === 'Backspace') {
        event.preventDefault();
        event.stopPropagation();

        if (currentNumberOfPositions <= 0) return;
        const lastPosition = currentValue.slice(-1);

        //   whatever the last char was, we remove 1
        let positionsToRemove = 1;

        //   if the last char was '.' we have custom logic
        if (lastPosition === ':') {
          // XY:
          // remove 2 to leave X
          if (currentNumberOfPositions === 3) {
            positionsToRemove = 2;
          }
          // X:
          // remove just 1 to leave X
          else if (currentNumberOfPositions === 2) {
            positionsToRemove = 1;
          }
        }
        const value = currentValue.slice(
          0,
          currentNumberOfPositions - positionsToRemove
        );
        customOnChange(value, field);
      }
      // for regular keys we just go with onChange
      // dont call stop propagation
      else {
      }
    };

    return (
      <PremiumInputContainer
        width={props.width}
        isDisabled={isDisabled}
        hasError={hasError}
        className={className}
        hasFocus={hasFocus}
        ref={clickRefFrom}
        onClick={() => {
          if (isDisabled) return;
          setHasFocus(true);
          setInputFocus();
        }}
      >
        {!showInput ? (
          <>
            <PremiumLabelBig>
              {label}
              {isRequired && <RequiredIndicator />}
            </PremiumLabelBig>
            <Invisible>
              <input
                {...props.form}
                type={'text'}
                id={id}
                value={value}
                ref={inputRef}
                disabled={isDisabled}
                {...props.field}
                onFocus={() => {
                  setHasFocus(true);
                  setInputFocus();
                }}
                onBlur={() => {
                  setHasFocus(false);
                }}
                onChange={(event) => {
                  const value = event.target.value;
                  customOnChange(value, field);
                }}
                onKeyDown={customOnKeyDown}
              />
            </Invisible>
            <PremiumUnit>{units} </PremiumUnit>
          </>
        ) : (
          <>
            <LabelAndValue>
              <PremiumLabelSmall htmlFor={id}>
                {label}
                {isRequired && <RequiredIndicator />}
              </PremiumLabelSmall>
              <Value>
                <input
                  {...props}
                  placeholder="--:--"
                  type={'text'}
                  id={id}
                  value={value}
                  ref={inputRef}
                  disabled={isDisabled}
                  {...props.form}
                  {...props.field}
                  onFocus={() => {
                    setHasFocus(true);
                    setInputFocus();
                  }}
                  onBlur={() => {
                    setHasFocus(false);
                  }}
                  onChange={(event) => {
                    const value = event.target.value;
                    customOnChange(value, field);
                  }}
                  onKeyDown={customOnKeyDown}
                />
              </Value>
            </LabelAndValue>
            {!!units && (
              <UnitsForLabelAndValue>
                <PremiumUnit>{units} </PremiumUnit>
              </UnitsForLabelAndValue>
            )}
          </>
        )}
      </PremiumInputContainer>
    );
  }

  //   search
  if (type === 'search') {
    return (
      <PremiumInputContainer
        width={props.width}
        isDisabled={isDisabled}
        hasError={hasError}
        className={className}
        hasFocus={hasFocus}
        ref={clickRefFrom}
        onClick={() => {
          if (isDisabled) return;
          setHasFocus(true);
          setInputFocus();
        }}
      >
        {!showInput ? (
          <>
            <PremiumLabelBig>
              {label}
              {isRequired && <RequiredIndicator />}
            </PremiumLabelBig>
            <Invisible>
              <input
                {...props.form}
                type={type}
                id={id}
                value={value}
                ref={inputRef}
                disabled={isDisabled}
                {...props.field}
                autoFocus={props.autoFocus}
                onFocus={() => {
                  setHasFocus(true);
                  setInputFocus();
                }}
                onBlur={() => {
                  setHasFocus(false);
                }}
              />
            </Invisible>
            <UnitsForLabelAndValue noPadding={true}>
              <PremiumUnit>
                <SearchIcon />
              </PremiumUnit>
            </UnitsForLabelAndValue>
          </>
        ) : (
          <>
            <LabelAndValue>
              <PremiumLabelSmall htmlFor={id}>
                {label}
                {isRequired && <RequiredIndicator />}
              </PremiumLabelSmall>
              <Value>
                <input
                  type={type}
                  id={id}
                  value={value}
                  ref={inputRef}
                  disabled={isDisabled}
                  {...props.form}
                  {...props.field}
                  {...props}
                  onFocus={() => {
                    setHasFocus(true);
                    setInputFocus();
                  }}
                  onBlur={() => {
                    setHasFocus(false);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      if (props.onEnter !== undefined) {
                        props.onEnter();
                      }
                    }
                  }}
                />
              </Value>
            </LabelAndValue>

            <UnitsForLabelAndValue noPadding={true}>
              <PremiumUnit>
                <SearchIcon />
              </PremiumUnit>
            </UnitsForLabelAndValue>
          </>
        )}
      </PremiumInputContainer>
    );
  }

  //    NUMBER
  if (type === 'number') {
    const setValue = (value, field) => {
      if (props.onChange !== undefined) {
        props.onChange(value);
      } else {
        form.setFieldValue(field.name, value);
      }
    };

    const internalOnChange = (event, field) => {
      const value = event.target.value;
      let formattedText;
      //   apply decimal format
      //   https://stackoverflow.com/questions/27740024/how-can-i-make-this-number-input-accept-commas
      var values = value.split('');
      var update = '';
      var transition = '';

      var expression = /(^\d+$)|(^\d+\.\d+$)|[,.]/;
      var finalExpression = new RegExp(`^([${min}-9][0-9]*[,.]?[0-9]{0,3})$`);

      for (id in values) {
        if (expression.test(values[id]) === true && values[id] !== '') {
          transition += '' + values[id].replace(',', '.');
          if (finalExpression.test(transition) === true) {
            update += '' + values[id].replace(',', '.');
          }
        }
      }
      formattedText = update;
      setValue(formattedText, field);
    };

    return (
      <PremiumInputContainer
        width={props.width}
        isDisabled={isDisabled}
        hasError={hasError}
        className={className}
        hasFocus={hasFocus}
        ref={clickRefFrom}
        onClick={() => {
          if (isDisabled) return;
          setHasFocus(true);
          setInputFocus();
        }}
      >
        {!showInput ? (
          <>
            <PremiumLabelBig>
              {label}
              {isRequired && <RequiredIndicator />}
            </PremiumLabelBig>
            <Invisible>
              <input
                {...props.form}
                type={'text'}
                id={id}
                value={value}
                ref={inputRef}
                disabled={isDisabled}
                {...props.field}
                onFocus={() => {
                  setHasFocus(true);
                  setInputFocus();
                }}
                onBlur={() => {
                  setHasFocus(false);
                }}
                onChange={(event) => {
                  internalOnChange(event, field);
                }}
              />
            </Invisible>
            <PremiumUnit>{units} </PremiumUnit>
          </>
        ) : (
          <>
            <LabelAndValue>
              <PremiumLabelSmall htmlFor={id}>
                {label}
                {isRequired && <RequiredIndicator />}
              </PremiumLabelSmall>
              <Value>
                <input
                  type={'text'}
                  id={id}
                  value={value}
                  ref={inputRef}
                  disabled={isDisabled}
                  {...props.form}
                  {...props.field}
                  {...props}
                  onFocus={() => {
                    setHasFocus(true);
                    setInputFocus();
                  }}
                  onBlur={() => {
                    setHasFocus(false);
                  }}
                  onChange={(event) => {
                    internalOnChange(event, field);
                  }}
                />
              </Value>
            </LabelAndValue>
            {!!units && (
              <UnitsForLabelAndValue>
                <PremiumUnit>{units} </PremiumUnit>
              </UnitsForLabelAndValue>
            )}
          </>
        )}
      </PremiumInputContainer>
    );
  }

  //   select with search
  if (type === 'select-with-search') {
    return <SelectWithSearchInput {...props} />;
  }

  //   TEXT
  return (
    <PremiumInputContainer
      width={props.width}
      isDisabled={isDisabled}
      hasError={hasError}
      className={className}
      hasFocus={hasFocus}
      ref={clickRefFrom}
      onClick={() => {
        if (isDisabled) return;
        setHasFocus(true);
        setInputFocus();
      }}
    >
      {!showInput ? (
        <>
          <PremiumLabelBig>
            {label}
            {isRequired && <RequiredIndicator />}
          </PremiumLabelBig>
          <Invisible>
            <input
              {...props.form}
              type={type}
              id={id}
              value={value}
              ref={inputRef}
              disabled={isDisabled}
              {...props.field}
              onFocus={() => {
                setHasFocus(true);
                setInputFocus();
              }}
              onBlur={() => {
                setHasFocus(false);
              }}
            />
          </Invisible>
          <PremiumUnit>{units} </PremiumUnit>
        </>
      ) : (
        <>
          <LabelAndValue>
            <PremiumLabelSmall htmlFor={id}>
              {label}
              {isRequired && <RequiredIndicator />}
            </PremiumLabelSmall>
            <Value>
              <input
                type={type}
                id={id}
                value={value}
                ref={inputRef}
                disabled={isDisabled}
                {...props.form}
                {...props.field}
                {...props}
                onFocus={() => {
                  setHasFocus(true);
                  setInputFocus();
                }}
                onBlur={() => {
                  setHasFocus(false);
                }}
              />
            </Value>
          </LabelAndValue>
          {!!units && (
            <UnitsForLabelAndValue>
              <PremiumUnit>{units} </PremiumUnit>
            </UnitsForLabelAndValue>
          )}
        </>
      )}
    </PremiumInputContainer>
  );
};

const PremiumInputWrapper = ({ ...props }) => {
  const { form, field, customErrorFieldName, showErrors, customHasError } =
    props;
  const id = field.name;
  const errorFieldName = customErrorFieldName ?? field.name;
  const errorMsg = _.get(form.errors, errorFieldName);
  const hasError = !!customHasError || (!!errorMsg && !!showErrors);
  const value = field.value;
  return <PremiumInput id={id} value={value} hasError={hasError} {...props} />;
};

export const FormikPremiumInput = ({ ...props }) => {
  return (
    <Field
      component={PremiumInputWrapper}
      {...props}
      style={{
        display: 'flex',
      }}
    />
  );
};
